import { NgTemplateOutlet } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, Component, Signal, computed, contentChildren, input, output, viewChild } from '@angular/core';
import { Swiper } from 'swiper/types';

import { SwiperElementOptions } from './swiper.types';

import { BaseComponent } from '../base/base.component';
import { SwiperSlideDirective } from './swiper-slide.directive';
import { SwiperDirective } from './swiper.directive';

/**
 * A themed Swiper carousel wrapping `<swiper-container>` (Swiper Element, core build).
 *
 * Encapsulates `CUSTOM_ELEMENTS_SCHEMA` so consumers stay schema-free by declaring their slides
 * as templates:
 *
 * ```html
 * <immo-swiper theme="gallery" [options]="{ spaceBetween: 24 }" (slideChange)="onSlideChange($event)">
 *     @for (image of images(); track image.id) {
 *         <ng-template immoSwiperSlide>
 *             <immo-image [image]="image" />
 *         </ng-template>
 *     }
 * </immo-swiper>
 * ```
 *
 * Raw `<swiper-slide>` children are also projected for consumers that already use
 * `CUSTOM_ELEMENTS_SCHEMA`. For full control over the container element, use the
 * `SwiperDirective` directly instead.
 */
@Component({
    selector: 'immo-swiper',
    templateUrl: './swiper.component.html',
    imports: [NgTemplateOutlet, SwiperDirective],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SwiperComponent extends BaseComponent {
    // -----------------------------------------------------------------------------------------------------
    // @ INPUT VARIABLES
    // -----------------------------------------------------------------------------------------------------

    /** The name of the theme preset to apply. Set to `''` to opt out of theming entirely. */
    readonly theme = input<string>('default');

    /** Per-instance options (incl. `modules` / `injectStyles`), merged over defaults and theme. */
    readonly options = input<SwiperElementOptions>({});

    // -----------------------------------------------------------------------------------------------------
    // @ OUTPUT VARIABLES
    // -----------------------------------------------------------------------------------------------------

    /** Emits the Swiper instance once, directly after initialization. */
    readonly swiperInit = output<Swiper>();

    readonly slideChange = output<Swiper>();

    readonly activeIndexChange = output<Swiper>();

    readonly reachBeginning = output<Swiper>();

    readonly reachEnd = output<Swiper>();

    // -----------------------------------------------------------------------------------------------------
    // @ CONTENT CHILDREN
    // -----------------------------------------------------------------------------------------------------

    protected readonly slides = contentChildren(SwiperSlideDirective);

    // -----------------------------------------------------------------------------------------------------
    // @ VIEW CHILDREN
    // -----------------------------------------------------------------------------------------------------

    protected readonly swiperDirective = viewChild(SwiperDirective);

    // -----------------------------------------------------------------------------------------------------
    // @ COMPUTED VARIABLES
    // -----------------------------------------------------------------------------------------------------

    /** The underlying Swiper instance, `null` until initialization has run (first browser render). */
    readonly swiper: Signal<Swiper | null> = computed(() => this.swiperDirective()?.swiper() ?? null);

    // -----------------------------------------------------------------------------------------------------
    // @ PUBLIC METHODS
    // -----------------------------------------------------------------------------------------------------

    /**
     * Slides to the next slide.
     *
     * @param speed - The transition duration in ms.
     * @returns {void}
     */
    slideNext(speed?: number): void {
        this.swiperDirective()?.slideNext(speed);
    }

    /**
     * Slides to the previous slide.
     *
     * @param speed - The transition duration in ms.
     * @returns {void}
     */
    slidePrev(speed?: number): void {
        this.swiperDirective()?.slidePrev(speed);
    }

    /**
     * Slides to the slide at the given index.
     *
     * @param index - The target slide index.
     * @param speed - The transition duration in ms.
     * @returns {void}
     */
    slideTo(index: number, speed?: number): void {
        this.swiperDirective()?.slideTo(index, speed);
    }

    /**
     * Recalculates the swiper's size and position, e.g. after slides were added or removed.
     *
     * @returns {void}
     */
    update(): void {
        this.swiperDirective()?.update();
    }
}
